import { type MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Backported React Router v6-style search params for v5
// Source: https://github.com/remix-run/react-router/blob/v6/packages/react-router-dom/dom.ts
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ParamKeyValuePair = [string, string];

export type URLSearchParamsInit = string | ParamKeyValuePair[] | Record<string, string | string[]> | URLSearchParams;

export const createSearchParams = (init: URLSearchParamsInit = ''): URLSearchParams =>
  new URLSearchParams(
    typeof init === 'string' || Array.isArray(init) || init instanceof URLSearchParams
      ? init
      : Object.keys(init).reduce((memo, key) => {
          const value = init[key];
          return memo.concat(Array.isArray(value) ? value.map((v) => [key, v]) : [[key, value]]);
        }, [] as ParamKeyValuePair[])
  );

export const getSearchParamsForLocation = (locationSearch: string, defaultSearchParams: URLSearchParams | null) => {
  const searchParams = createSearchParams(locationSearch);

  if (defaultSearchParams) {
    // Use `defaultSearchParams.forEach(...)` here instead of iterating of
    // `defaultSearchParams.keys()` to work-around a bug in Firefox related to
    // web extensions. Relevant Bugzilla tickets:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1414602
    // https://bugzilla.mozilla.org/show_bug.cgi?id=1023984
    defaultSearchParams.forEach((_, key) => {
      if (!searchParams.has(key)) {
        defaultSearchParams.getAll(key).forEach((value) => {
          searchParams.append(key, value);
        });
      }
    });
  }

  return searchParams;
};

export type SetURLSearchParams = (
  nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
  navigateOptions?: {
    replace?: boolean;
    /** Adds persistent client side routing state to the next location */
    state?: any;
  }
) => void;

/** `staged` is `URLSearchParams.toString()` (no leading `?`). */
const baseSearchFromStagedOrHistory = (staged: string | null, historySearch: string): string => {
  if (staged === null) {
    return historySearch;
  }
  return staged === '' ? '' : `?${staged}`;
};

const scheduleStagedQueryReset = (
  clearScheduledRef: MutableRefObject<boolean>,
  stagedQueryRef: MutableRefObject<string | null>
) => {
  if (clearScheduledRef.current) {
    return;
  }
  clearScheduledRef.current = true;
  queueMicrotask(() => {
    clearScheduledRef.current = false;
    stagedQueryRef.current = null;
  });
};

/**
 * Read/write the current location's query string as `URLSearchParams`.
 *
 * Functional updates are applied against the latest URL (see `baseSearchFromStagedOrHistory`):
 * multiple `setSearchParams(draft => …)` calls in one interaction must compose, e.g. Fluent
 * multiselect firing several `onChange` handlers before React re-renders.
 */
export const useSearchParams = (defaultInit?: URLSearchParamsInit): [URLSearchParams, SetURLSearchParams] => {
  const defaultSearchParamsRef = useRef(createSearchParams(defaultInit));
  const hasSetSearchParamsRef = useRef(false);
  const stagedQueryRef = useRef<string | null>(null);
  const stagedClearScheduledRef = useRef(false);

  const location = useLocation();
  const searchParams = useMemo(() => {
    // Only merge defaults until the first `setSearchParams`; after that, defaults must not
    // win over an intentional `{}` clear (matches react-router-dom v6 behavior).
    return getSearchParamsForLocation(
      location.search,
      hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
    );
  }, [location.search]);

  const history = useHistory();
  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, navigateOptions) => {
      const baseSearch = baseSearchFromStagedOrHistory(stagedQueryRef.current, history.location.search);
      const liveParams = getSearchParamsForLocation(
        baseSearch,
        hasSetSearchParamsRef.current ? null : defaultSearchParamsRef.current
      );

      const newSearchParams = createSearchParams(
        typeof nextInit === 'function'
          ? (() => {
              const draft = new URLSearchParams(liveParams.toString());
              return nextInit(draft) ?? draft;
            })()
          : nextInit
      );
      const newSearch = newSearchParams.toString();
      if (newSearch === liveParams.toString()) {
        return;
      }

      stagedQueryRef.current = newSearch;
      hasSetSearchParamsRef.current = true;
      const navigateMethod = navigateOptions?.replace ? history.replace : history.push;
      navigateMethod({ search: newSearch }, navigateOptions?.state);

      scheduleStagedQueryReset(stagedClearScheduledRef, stagedQueryRef);
    },
    [history]
  );

  return [searchParams, setSearchParams];
};
